/**
 * usePermissions - Hook للتحقق من صلاحيات المستخدم الحالي
 * يُطابق تماماً ROLE_PERMISSIONS في backend/core/security.py
 */
import { useAuth } from '../context/AuthContext';

// نسخة من ROLE_PERMISSIONS الموجودة في الـ Backend
const ROLE_PERMISSIONS = {
  admin:      ['*'],
  manager: [
    'dashboard:read',
    'customers:read', 'customers:write', 'customers:delete',
    'suppliers:read', 'suppliers:write', 'suppliers:delete',
    'products:read',  'products:write',  'products:delete',
    'warehouses:read','warehouses:write','warehouses:delete',
    'stock_moves:read','stock_moves:write',
    'invoices:read',  'invoices:write',  'invoices:delete',
    'sales:read',     'sales:write',
    'purchases:read', 'purchases:write',
    'quotations:read','quotations:write','quotations:delete',
    'purchase_orders:read','purchase_orders:write',
    'crm:read',       'crm:write',
    'manufacturing:read','manufacturing:write',
    'employees:read',
    'expenses:read',  'expenses:write',
    'accounting:read',
    'reports:read',
    'ai_assistant:read',
    'workflows:read', 'workflows:write', 'workflows:delete',
    'strategy:read', 'strategy:write', 'strategy:delete',
    'projects:read', 'projects:write', 'projects:delete',
    'settings:read',
    'payroll:read', 'payroll:approve', 'payroll:process',
  ],
  accountant: [
    'dashboard:read',
    'invoices:read', 'invoices:write', 'invoices:delete',
    'accounting:read', 'accounting:write', 'accounting:delete',
    'reports:read',
    'expenses:read',
    'customers:read',
    'suppliers:read',
    'payroll:read', 'payroll:process', 'payroll:disburse',
    'payroll:read_sensitive', 'banking:read_sensitive',
  ],
  sales: [
    'dashboard:read',
    'customers:read', 'customers:write', 'customers:delete',
    'crm:read', 'crm:write', 'crm:delete',
    'invoices:read', 'invoices:write',
    'quotations:read', 'quotations:write', 'quotations:delete',
    'sales:read', 'sales:write',
    'products:read',
  ],
  warehouse: [
    'dashboard:read',
    'products:read',  'products:write', 'products:delete',
    'warehouses:read','warehouses:write','warehouses:delete',
    'stock_moves:read','stock_moves:write',
    'manufacturing:read',
  ],
  hr: [
    'dashboard:read',
    'employees:read', 'employees:write', 'employees:delete',
    'expenses:read',  'expenses:write',  'expenses:delete',
    'payroll:read', 'payroll:write', 'payroll:approve', 'payroll:process',
    'payroll:disburse', 'payroll:read_sensitive',
  ],
  viewer: [
    'dashboard:read',
    'reports:read',
    'customers:read',
    'products:read',
    'invoices:read',
    'sales:read',
  ],
  user: [
    'dashboard:read',
    'customers:read',
    'products:read',
    'invoices:read',
  ],
};

/**
 * التحقق إذا كان للدور صلاحية معينة
 * @param {string} role - دور المستخدم
 * @param {string} resource - الوحدة (customers, invoices ...)
 * @param {string} action - الإجراء (read, write, delete)
 */
function hasPermission(role, resource, action) {
  const perms = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS['user'];
  if (perms.includes('*')) return true;
  if (perms.includes(`${resource}:*`)) return true;
  if (perms.includes(`${resource}:${action}`)) return true;
  return false;
}

/**
 * Hook الرئيسي
 * الاستخدام:
 *   const { can, isAdmin, role } = usePermissions();
 *   if (can('invoices', 'write')) { ... }
 */
export function usePermissions() {
  const { user } = useAuth();
  const role = user?.role || 'viewer';

  return {
    role,
    isAdmin:      role === 'admin',
    isManager:    role === 'manager',
    isAccountant: role === 'accountant',
    isSales:      role === 'sales',
    isWarehouse:  role === 'warehouse',
    isHR:         role === 'hr',

    /** can('invoices', 'write') → true/false */
    can: (resource, action = 'read') => hasPermission(role, resource, action),

    /** canRead('customers') */
    canRead:   (resource) => hasPermission(role, resource, 'read'),

    /** canWrite('invoices') */
    canWrite:  (resource) => hasPermission(role, resource, 'write'),

    /** canDelete('products') */
    canDelete: (resource) => hasPermission(role, resource, 'delete'),

    /** canAny(['admin','manager']) */
    canAny: (roles) => roles.includes(role),
  };
}

/**
 * مكوّن الحماية - يخفي المحتوى إذا لم تكن هناك صلاحية
 * الاستخدام:
 *   <PermissionGate resource="users" action="write">
 *     <button>إضافة مستخدم</button>
 *   </PermissionGate>
 */
export function PermissionGate({ resource, action = 'read', roles, fallback = null, children }) {
  const { can, canAny, role } = usePermissions();

  // إذا حدّدنا قائمة أدوار
  if (roles && !canAny(roles)) return fallback;

  // إذا حدّدنا resource وaction
  if (resource && !can(resource, action)) return fallback;

  return children;
}
